import JSZip from "jszip";
import { saveAs } from "file-saver";

const TEMPLATE_URL = `${process.env.PUBLIC_URL || ""}/templates/container-loading-report-template.docx`;

// Template picture width is fixed by the original document layout (3.41 inch).
const SLOT_WIDTH_EMU = 3122295;
// Tallest picture box used by the original report; keeps portrait photos on the page.
const SLOT_MAX_HEIGHT_EMU = 3511550;

// "View of Container Numbered" bölümü artık sabit bir slot listesi değil:
// şablondaki tek tablo, seçilen her konteyner için klonlanıp kendi numarası
// ve kendi fotoğraflarıyla dolduruluyor (bkz. buildContainerViewBlock).
// Bir konteyner blokta en fazla bu kadar fotoğraf slotu vardır.
export const CONTAINER_VIEW_SLOT_COUNT = 6;

/**
 * Photo slots of the template, in document order.
 * `target` is the media file inside the docx that the slot renders.
 */
export const REPORT_SECTIONS = [
  {
    key: "cargoes",
    title: "View of Cargoes",
    label: "Yük Görselleri",
    description: "Paketlenmiş boruların / yükün genel görünümü.",
    slots: [
      { rId: "rId20", target: "word/media/image8.jpeg" },
      { rId: "rId21", target: "word/media/image9.jpeg" },
      { rId: "rId22", target: "word/media/image10.jpeg" },
      { rId: "rId23", target: "word/media/image11.jpeg" },
    ],
  },
  {
    key: "lashing1",
    title: "View of Lashing & Dunnages",
    label: "Lashing & Takoz Görselleri (1)",
    description: "Kayış, takoz ve kraft kağıt uygulamasının ilk fotoğraf seti.",
    slots: [
      { rId: "rId26", target: "word/media/image12.jpeg" },
      { rId: "rId27", target: "word/media/image13.jpeg" },
      { rId: "rId28", target: "word/media/image14.jpeg" },
      { rId: "rId29", target: "word/media/image15.jpeg" },
    ],
  },
  {
    key: "lashing2",
    title: "View of Lashing & Dunnages",
    label: "Lashing & Takoz Görselleri (2)",
    description: "Kayış, takoz ve kraft kağıt uygulamasının ikinci fotoğraf seti.",
    slots: [
      { rId: "rId30", target: "word/media/image16.jpeg" },
      { rId: "rId31", target: "word/media/image17.jpeg" },
      { rId: "rId32", target: "word/media/image18.jpeg" },
      { rId: "rId33", target: "word/media/image19.jpeg" },
      { rId: "rId34", target: "word/media/image20.jpeg" },
      { rId: "rId35", target: "word/media/image21.jpeg" },
    ],
  },
  {
    key: "moisture",
    title: "View of Moisture Bags",
    label: "Nem Alıcı Görselleri",
    description: "Konteyner içine yerleştirilen nem alıcı torbalar.",
    slots: [
      { rId: "rId36", target: "word/media/image22.jpeg" },
      { rId: "rId37", target: "word/media/image23.jpeg" },
      { rId: "rId38", target: "word/media/image24.jpeg" },
      { rId: "rId39", target: "word/media/image25.jpeg" },
    ],
  },
  {
    key: "stuffing",
    title: "View of Stuffing Operation",
    label: "Yükleme Operasyonu Görselleri",
    description: "Vinç / aparat ile yükleme anına ait fotoğraflar.",
    slots: [
      { rId: "rId42", target: "word/media/image26.jpeg" },
      { rId: "rId43", target: "word/media/image27.jpeg" },
      { rId: "rId44", target: "word/media/image28.jpeg" },
      { rId: "rId45", target: "word/media/image29.jpeg" },
    ],
  },
];

export const REPORT_TEXT_FIELDS = [
  { key: "DATE", label: "Tarih (rapor üst bilgisi)" },
  { key: "SUBJECT", label: "Subject", multiline: true },
  { key: "INTRO", label: "Giriş cümlesi", multiline: true },
  { key: "CARGO", label: "Cargo" },
  { key: "LOAD_PORT", label: "Load Port" },
  { key: "DISCHARGE_PORT", label: "Discharge Port" },
  { key: "SHIPPER", label: "Shipper" },
  { key: "CUSTOMER", label: "Customer" },
  { key: "QUANTITY", label: "Quantity" },
  { key: "TOTAL_QUANTITY", label: "Total Quantity" },
];

// "B - Loaded Cargo Quantity As Per Container Number" tablosunda artık
// konteyner başına bir satır var; No ve Container No otomatik (sıra ve
// kayıttan), bu alanlar operatörün her konteyner için elle gireceği sütunlar.
export const REPORT_TABLE_FIELDS = [
  { key: "HUMIDITY", label: "Humidity" },
  { key: "BOOKING_NO", label: "Booking No" },
  { key: "BUNDLES", label: "No of Bundles" },
  { key: "WEIGHT", label: "Weight (Gross)" },
  { key: "REF_NO", label: "REF No" },
];

export const REPORT_BULLET_FIELDS = [
  { key: "C1_1", group: "C1 - CONDITION OF THE CONTAINERS BEFORE STUFFING" },
  { key: "C1_2", group: "C1 - CONDITION OF THE CONTAINERS BEFORE STUFFING" },
  { key: "C1_3", group: "C1 - CONDITION OF THE CONTAINERS BEFORE STUFFING" },
  { key: "C1_4", group: "C1 - CONDITION OF THE CONTAINERS BEFORE STUFFING" },
  { key: "C2_1", group: "C2 - CONDITION OF LASHING & DUNNAGING" },
  { key: "C2_2", group: "C2 - CONDITION OF LASHING & DUNNAGING" },
  { key: "C2_3", group: "C2 - CONDITION OF LASHING & DUNNAGING" },
  { key: "C2_4", group: "C2 - CONDITION OF LASHING & DUNNAGING" },
  { key: "C3_1", group: "C3 - CARGO STUFFING PROCEDURE" },
  { key: "C3_2", group: "C3 - CARGO STUFFING PROCEDURE" },
];

function escapeXml(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function fillPlaceholders(xml, values) {
  return xml.replace(/\{\{([A-Z0-9_]+)\}\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(values, key)
      ? escapeXml(values[key])
      : match
  );
}

// Decoding + re-encoding full-size camera photos is the slow part of the
// report, so it runs a few at a time (parallel) instead of one by one, and
// the output is only as large as a print-size picture box actually needs.
const PHOTO_CONCURRENCY = 4;
const PHOTO_LONG_EDGE_PX = 1100;
const PHOTO_JPEG_QUALITY = 0.8;

export function createLimiter(limit) {
  let active = 0;
  const queue = [];

  const next = () => {
    if (active >= limit || queue.length === 0) return;

    active += 1;
    const { task, resolve, reject } = queue.shift();

    task()
      .then(resolve, reject)
      .finally(() => {
        active -= 1;
        next();
      });
  };

  return (task) =>
    new Promise((resolve, reject) => {
      queue.push({ task, resolve, reject });
      next();
    });
}

const limitPhotoWork = createLimiter(PHOTO_CONCURRENCY);

/** Decodes a blob off the main thread when possible; falls back to <img>. */
async function decodeImage(blob) {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(blob, {
        imageOrientation: "from-image",
      });

      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close && bitmap.close(),
      };
    } catch (error) {
      // fall through to <img>
    }
  }

  const url = URL.createObjectURL(blob);

  try {
    const image = new Image();

    image.src = url;
    await image.decode();

    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      release: () => URL.revokeObjectURL(url),
    };
  } catch (error) {
    URL.revokeObjectURL(url);
    return null;
  }
}

/**
 * Re-encodes the complete photo at print size without adding a background,
 * cropping or discarding any source pixels. Word then stretches the complete
 * frame to the template slot so portrait photos also use the available width.
 */
async function resizePhotoWithoutCropping(
  blob,
  targetLongEdge = PHOTO_LONG_EDGE_PX,
  quality = PHOTO_JPEG_QUALITY
) {
  const decoded = await decodeImage(blob);

  if (!decoded) return blob;

  const { source, width: srcWidth, height: srcHeight } = decoded;
  const { width: outWidth, height: outHeight } =
    calculateUncroppedPixelSize(srcWidth, srcHeight, targetLongEdge);

  const canvas = document.createElement("canvas");

  canvas.width = outWidth;
  canvas.height = outHeight;

  canvas
    .getContext("2d")
    .drawImage(source, 0, 0, srcWidth, srcHeight, 0, 0, outWidth, outHeight);

  decoded.release();

  const result = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", quality)
  );

  return result || blob;
}

export function calculateUncroppedPixelSize(
  srcWidth,
  srcHeight,
  targetLongEdge = PHOTO_LONG_EDGE_PX
) {
  if (
    !Number.isFinite(srcWidth) ||
    !Number.isFinite(srcHeight) ||
    srcWidth <= 0 ||
    srcHeight <= 0
  ) {
    return { width: 1, height: 1 };
  }

  const scale = Math.min(1, targetLongEdge / Math.max(srcWidth, srcHeight));

  return {
    width: Math.max(1, Math.round(srcWidth * scale)),
    height: Math.max(1, Math.round(srcHeight * scale)),
  };
}

/** Reads the `<wp:extent>` box (in EMU) a drawing run already renders at. */
function extractRunExtent(runXml) {
  const match = runXml && runXml.match(/<wp:extent cx="(\d+)" cy="(\d+)"/);

  if (!match) {
    return { cx: SLOT_WIDTH_EMU, cy: SLOT_MAX_HEIGHT_EMU };
  }

  return { cx: Number(match[1]), cy: Number(match[2]) };
}

/** Finds the `<w:r>…</w:r>` that wraps the drawing using the given relationship id. */
function findDrawingRun(xml, rId) {
  const blipIndex = xml.indexOf(`r:embed="${rId}"`);

  if (blipIndex === -1) return null;

  const start = xml.lastIndexOf("<w:r>", blipIndex);
  const startWithProps = xml.lastIndexOf("<w:r ", blipIndex);
  const runStart = Math.max(start, startWithProps);

  if (runStart === -1) return null;

  const runEnd = xml.indexOf("</w:r>", blipIndex);

  if (runEnd === -1) return null;

  return { start: runStart, end: runEnd + "</w:r>".length };
}

async function fetchPhotoBlob(url) {
  const response = await fetch(url, { credentials: "include" });

  if (!response.ok) {
    throw new Error(`Fotoğraf indirilemedi (${response.status}): ${url}`);
  }

  return response.blob();
}

/** Downloads a photo and preserves its complete, original frame. */
function prepareUncroppedPhoto(url) {
  return limitPhotoWork(async () => {
    const original = await fetchPhotoBlob(url);

    return resizePhotoWithoutCropping(original);
  });
}

export function stripHumidityUnit(value) {
  return String(value == null ? "" : value)
    .trim()
    .replace(/\s*g\s*\/\s*m(?:3|³)\s*$/i, "")
    .trim();
}

export function formatHumidityWithUnit(value) {
  const cleanValue = stripHumidityUnit(value);

  return `${cleanValue ? `${cleanValue} ` : ""}g/m3`;
}

const EMU_PER_TWIP = 635;
// Word's default table cell padding (left + right), in twips.
const DEFAULT_CELL_PADDING_TWIPS = 216;

/**
 * The template's picture boxes are a hair wider than the table cell that
 * holds them (cell width - padding - paragraph indent), so they spill over
 * the cell border and never look "seated". Reads the enclosing cell and
 * returns the picture box that exactly fills its width, keeping the box's
 * designed proportions.
 */
function fitBoxToCell(xml, run) {
  const runXml = xml.slice(run.start, run.end);
  const { cx, cy } = extractRunExtent(runXml);
  const cellStart = xml.lastIndexOf("<w:tc>", run.start);

  if (cellStart === -1) return { cx, cy };

  const beforeRun = xml.slice(cellStart, run.start);
  const widthMatch = beforeRun.match(/<w:tcW w:w="(\d+)"/);

  if (!widthMatch) return { cx, cy };

  const indentMatches = [...beforeRun.matchAll(/<w:ind w:left="(\d+)"/g)];
  const indent = indentMatches.length
    ? Number(indentMatches[indentMatches.length - 1][1])
    : 0;

  const availableTwips =
    Number(widthMatch[1]) - DEFAULT_CELL_PADDING_TWIPS - indent;

  if (availableTwips <= 0) return { cx, cy };

  const fittedCx = Math.floor(availableTwips * EMU_PER_TWIP);

  return { cx: fittedCx, cy: Math.round((cy * fittedCx) / cx) };
}

/** Rewrites the picture box to cx × cy (EMU) and centers it in its cell. */
function resizeRun(xml, run, cx, cy) {
  const resized = xml
    .slice(run.start, run.end)
    .replace(/<wp:extent cx="\d+" cy="\d+"/, `<wp:extent cx="${cx}" cy="${cy}"`)
    .replace(/<a:ext cx="\d+" cy="\d+"/, `<a:ext cx="${cx}" cy="${cy}"`)
    .replace(/<wp:effectExtent [^>]*\/>/, '<wp:effectExtent l="0" t="0" r="0" b="0"/>');

  let updatedXml = xml.slice(0, run.start) + resized + xml.slice(run.end);
  const beforeRun = updatedXml.slice(0, run.start);
  const paragraphMatches = [
    ...beforeRun.matchAll(/<w:p(?:\s[^>]*)?>/g),
  ];
  const paragraphStart = paragraphMatches.length
    ? paragraphMatches[paragraphMatches.length - 1].index
    : -1;

  if (paragraphStart === -1) return updatedXml;

  const paragraphOpenEnd = updatedXml.indexOf(">", paragraphStart) + 1;
  const paragraphEnd = updatedXml.indexOf("</w:p>", run.start);
  const propertiesStart = updatedXml.indexOf("<w:pPr>", paragraphOpenEnd);

  if (
    propertiesStart !== -1 &&
    propertiesStart < paragraphEnd &&
    propertiesStart < run.start
  ) {
    const propertiesEnd = updatedXml.indexOf("</w:pPr>", propertiesStart);
    const properties = updatedXml.slice(
      propertiesStart,
      propertiesEnd + "</w:pPr>".length
    );
    const centeredProperties = /<w:jc\b[^>]*\/>/.test(properties)
      ? properties.replace(/<w:jc\b[^>]*\/>/, '<w:jc w:val="center"/>')
      : properties.replace("</w:pPr>", '<w:jc w:val="center"/></w:pPr>');

    updatedXml =
      updatedXml.slice(0, propertiesStart) +
      centeredProperties +
      updatedXml.slice(propertiesEnd + "</w:pPr>".length);
  } else {
    updatedXml =
      updatedXml.slice(0, paragraphOpenEnd) +
      '<w:pPr><w:jc w:val="center"/></w:pPr>' +
      updatedXml.slice(paragraphOpenEnd);
  }

  return updatedXml;
}

const CONTAINER_HEADING_ANCHOR =
  "View of Container Numbered with {{CONTAINER_NO}}";
// Adjacent tables merge in Word, so a paragraph must separate the copies; keep
// it ~1pt tall so it never pushes a page-filling table onto an extra page.
const TABLE_SEPARATOR =
  '<w:p><w:pPr><w:spacing w:before="0" w:after="0" w:line="20" w:lineRule="exact"/></w:pPr></w:p>';
const CONTAINER_ROW_ANCHOR = "{{ROW_NO}}";

/** Extracts the single `<w:tbl>…</w:tbl>` that renders one container's photo view. */
function extractContainerViewChunk(documentXml) {
  const headingIdx = documentXml.indexOf(CONTAINER_HEADING_ANCHOR);

  if (headingIdx === -1) {
    throw new Error(
      "Şablonda 'View of Container Numbered' bölümü bulunamadı."
    );
  }

  const start = documentXml.lastIndexOf("<w:tbl>", headingIdx);
  const end = documentXml.indexOf("</w:tbl>", headingIdx) + "</w:tbl>".length;

  if (start === -1 || end === -1) {
    throw new Error("Konteyner görünümü tablosunun sınırları bulunamadı.");
  }

  // The template positions this table as floating/anchored text ("wrap
  // text" in Word), at a fixed offset relative to its own anchor paragraph.
  // Cloning it as-is for every container makes each copy anchor near the
  // same spot and the copies render on top of one another. Stripping the
  // positioning turns it into a normal block table that simply stacks after
  // the previous one, which is what we want for N repeated copies.
  const template = documentXml
    .slice(start, end)
    .replace(/<w:tblpPr\b[^>]*\/>/, "");

  return { start, end, template };
}

/** Extracts the single `<w:tr>…</w:tr>` data row of the "B" cargo quantity table. */
function extractContainerRowChunk(documentXml) {
  const anchorIdx = documentXml.indexOf(CONTAINER_ROW_ANCHOR);

  if (anchorIdx === -1) {
    throw new Error("Şablonda konteyner tablosu satırı bulunamadı.");
  }

  // Plain lastIndexOf("<w:tr", …) would also match "<w:trPr" / "<w:trHeight"
  // (both start with the same 5 characters) and grab a truncated, invalid
  // chunk; only "<w:tr>" or "<w:tr " is the actual row-opening tag.
  const rowOpenMatches = [
    ...documentXml.slice(0, anchorIdx).matchAll(/<w:tr(?=[ >])/g),
  ];
  const start =
    rowOpenMatches.length > 0
      ? rowOpenMatches[rowOpenMatches.length - 1].index
      : -1;
  const end = documentXml.indexOf("</w:tr>", anchorIdx) + "</w:tr>".length;

  if (start === -1 || end === -1) {
    throw new Error("Konteyner tablosu satırının sınırları bulunamadı.");
  }

  return { start, end, template: documentXml.slice(start, end) };
}

function getMaxNumericMatch(xml, pattern) {
  const numbers = [...xml.matchAll(pattern)].map((match) => Number(match[1]));

  return numbers.length ? Math.max(...numbers) : 0;
}

function createIdAllocator(relsXml) {
  let ridCounter = getMaxNumericMatch(relsXml, /Id="rId(\d+)"/g) + 1;
  let imageCounter =
    getMaxNumericMatch(relsXml, /Target="media\/image(\d+)\.jpeg"/g) + 1;
  // Large, clearly-out-of-range base so cloned docPr ids can never collide
  // with the template's own (pseudo-random, but much smaller) ids.
  let docPrCounter = 2000000000;

  return {
    nextRid: () => `rId${ridCounter++}`,
    nextMediaTarget: () => `word/media/image${imageCounter++}.jpeg`,
    nextDocPrId: () => String(docPrCounter++),
  };
}

/**
 * Clones the "View of Container Numbered" table for one container: fills in
 * its number, embeds up to CONTAINER_VIEW_SLOT_COUNT chosen photos and drops
 * the picture entirely for any slot the operator left empty.
 */
async function buildContainerViewBlock({
  template,
  slotRIds,
  container,
  startOnNewPage,
  idAllocator,
  relationshipEntries,
  zip,
}) {
  let blockXml = template.replace(
    /\{\{CONTAINER_NO\}\}/g,
    escapeXml(container.containerNo)
  );

  // Every cloned copy carries the same docPr ids as the template; renumber
  // them so Word doesn't see duplicate drawing ids across container blocks.
  blockXml = blockXml.replace(/<wp:docPr id="\d+"/g, () =>
    `<wp:docPr id="${idAllocator.nextDocPrId()}"`
  );

  // Cloned paragraphs/drawings also repeat the template's w14:paraId /
  // wp14:anchorId values; duplicates confuse Word's layout, so drop them
  // (they are optional attributes).
  blockXml = blockXml.replace(
    /\s+(?:w14:paraId|w14:textId|wp14:anchorId|wp14:editId)="[^"]*"/g,
    ""
  );

  // Each container's photo table fills (almost) a whole page. Force every
  // copy after the first onto its own page so consecutive copies can never
  // spill into / overlap one another.
  if (startOnNewPage) {
    blockXml = blockXml.replace(
      /(<w:pPr>\s*(?:<w:pStyle\b[^>]*\/>)?)/,
      "$1<w:pageBreakBefore/>"
    );
  }

  const photos = Array.isArray(container.photos) ? container.photos : [];
  const preparedPhotos = await Promise.all(
    slotRIds.map((_, index) => {
      const url = photos[index];

      return url ? prepareUncroppedPhoto(url) : null;
    })
  );

  for (let index = 0; index < slotRIds.length; index += 1) {
    const oldRid = slotRIds[index];
    const url = photos[index];
    const prepared = preparedPhotos[index];
    let run = findDrawingRun(blockXml, oldRid);

    if (!url || !prepared) {
      if (run) {
        blockXml = blockXml.slice(0, run.start) + blockXml.slice(run.end);
      }

      continue;
    }

    // Fill the biggest box the template cell allows. The complete source
    // frame remains present; Word widens portrait photos instead of leaving
    // empty space beside them.
    let maxBox = { cx: SLOT_WIDTH_EMU, cy: SLOT_MAX_HEIGHT_EMU };

    if (run) {
      maxBox = fitBoxToCell(blockXml, run);
    }

    const box = maxBox;

    if (run) {
      blockXml = resizeRun(blockXml, run, box.cx, box.cy);
    }

    const newRid = idAllocator.nextRid();
    const mediaTarget = idAllocator.nextMediaTarget();

    blockXml = blockXml.replace(`r:embed="${oldRid}"`, `r:embed="${newRid}"`);

    zip.file(mediaTarget, prepared, { compression: "STORE" });
    relationshipEntries.push(
      `<Relationship Id="${newRid}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="${mediaTarget.replace("word/", "")}"/>`
    );
  }

  return blockXml;
}

/**
 * Builds the CONTAINER LOADING REPORT docx from the shipped template.
 *
 * One docx is produced for every selected container: the "View of Container
 * Numbered" block and the "B" quantity-table row are repeated once per
 * container (in `containers` order); the other photo sections (cargoes,
 * lashing, moisture, stuffing) stay a single shared set for the whole
 * shipment.
 *
 * @param {object} params
 * @param {object} params.values        shared placeholder key -> text
 * @param {object[]} params.containers  [{ containerNo, humidity, bookingNo, bundles, weight, refNo, photos: string[] }]
 * @param {object} params.selections    shared section key -> array of photo urls (in order)
 * @param {string} params.fileName      download name
 * @param {(step: string) => void} [params.onProgress]
 */
export async function generateContainerLoadingReport({
  values,
  containers,
  selections,
  fileName,
  onProgress = () => {},
}) {
  const containerList = Array.isArray(containers) ? containers : [];

  if (containerList.length === 0) {
    throw new Error("Rapor için en az bir konteyner seçilmelidir.");
  }

  onProgress("Şablon indiriliyor...");

  const templateResponse = await fetch(TEMPLATE_URL);

  if (!templateResponse.ok) {
    throw new Error(
      "Rapor şablonu bulunamadı (public/templates/container-loading-report-template.docx)."
    );
  }

  const zip = await JSZip.loadAsync(await templateResponse.arrayBuffer());

  let documentXml = await zip.file("word/document.xml").async("string");
  const relsPath = "word/_rels/document.xml.rels";
  let relsXml = await zip.file(relsPath).async("string");

  // Extracted from the pristine template, before either gets spliced out.
  const containerView = extractContainerViewChunk(documentXml);
  const containerRow = extractContainerRowChunk(documentXml);
  const containerSlotRIds = [
    ...containerView.template.matchAll(/r:embed="(rId\d+)"/g),
  ].map((match) => match[1]);

  const idAllocator = createIdAllocator(relsXml);
  const relationshipEntries = [];

  onProgress(`${containerList.length} konteyner için fotoğraflar hazırlanıyor...`);

  // Blocks are built concurrently; the shared photo limiter keeps the number
  // of photos being decoded at once bounded, and XML edits are synchronous.
  const containerBlocks = await Promise.all(
    containerList.map((container, index) =>
      buildContainerViewBlock({
        template: containerView.template,
        slotRIds: containerSlotRIds,
        container,
        startOnNewPage: index > 0,
        idAllocator,
        relationshipEntries,
        zip,
      })
    )
  );

  const rowReplacement = containerList
    .map((container, index) =>
      fillPlaceholders(containerRow.template, {
        ROW_NO: String(index + 1),
        CONTAINER_NO: container.containerNo,
        HUMIDITY: formatHumidityWithUnit(container.humidity),
        BOOKING_NO: container.bookingNo,
        BUNDLES: container.bundles,
        WEIGHT: container.weight,
        REF_NO: container.refNo,
      })
    )
    .join("");

  // Splice the later section first so the row chunk's (earlier) offsets stay valid.
  documentXml =
    documentXml.slice(0, containerView.start) +
    containerBlocks.join(TABLE_SEPARATOR) +
    documentXml.slice(containerView.end);

  documentXml =
    documentXml.slice(0, containerRow.start) +
    rowReplacement +
    documentXml.slice(containerRow.end);

  onProgress("Ortak fotoğraflar hazırlanıyor...");

  // Filled slots keep their original rId/target (only the media file and
  // the picture box change); empty ones are dropped further down.
  const pendingShared = [];
  const filledSlots = new Map();

  for (const section of REPORT_SECTIONS) {
    const urls = Array.isArray(selections?.[section.key])
      ? selections[section.key]
      : [];

    for (let index = 0; index < section.slots.length; index += 1) {
      const slot = section.slots[index];
      const url = urls[index];
      const run = url ? findDrawingRun(documentXml, slot.rId) : null;

      if (!url || !run) {
        filledSlots.set(slot.rId, null);
        continue;
      }

      const maxBox = fitBoxToCell(documentXml, run);

      pendingShared.push(
        prepareUncroppedPhoto(url).then((blob) => {
          filledSlots.set(slot.rId, maxBox);
          zip.file(slot.target, blob, { compression: "STORE" });
        })
      );
    }
  }

  await Promise.all(pendingShared);

  onProgress("Word dosyası oluşturuluyor...");

  // Slots are edited in place (their rId/target stay); empty ones lose the
  // picture entirely, filled ones occupy the complete template slot.
  for (const section of REPORT_SECTIONS) {
    for (const slot of section.slots) {
      const box = filledSlots.get(slot.rId);
      const run = findDrawingRun(documentXml, slot.rId);

      if (!run) continue;

      if (!box) {
        documentXml =
          documentXml.slice(0, run.start) + documentXml.slice(run.end);
      } else {
        documentXml = resizeRun(documentXml, run, box.cx, box.cy);
      }
    }
  }

  documentXml = fillPlaceholders(documentXml, values || {});

  if (relationshipEntries.length > 0) {
    relsXml = relsXml.replace(
      "</Relationships>",
      `${relationshipEntries.join("")}</Relationships>`
    );
  }

  zip.file("word/document.xml", documentXml);
  zip.file(relsPath, relsXml);

  const output = await zip.generateAsync({
    type: "blob",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  });

  saveAs(output, fileName || "CONTAINER LOADING REPORT.docx");

  onProgress("Tamamlandı.");

  return output;
}
